import { ReactElement, useCallback, useEffect, useState } from "react";
import { Preferences } from '@capacitor/preferences';
import PlanSelection, { FormInput, PlanSelectionProps } from "./PlanSelection";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatchType, RootState, actions } from "../../../store";
import { 
  BILLING_INTERVAL, PAGE_URL, TIER, NEW_TIER, PRICING, EVENT_TYPE, NEW_TIER_DETAILS 
} from "../../../constants";
import { getCheckoutSessionId, redirectToCheckout, trackPurchase } from "../../../utils/paymentUtils";
import { trackEventWithExtra } from "../../../utils/appAnalyticsUtils";
import { useNavigate } from "react-router";

const comparisonRows: PlanSelectionProps['comparisonRows'] = [
  { label: 'Leaders per seat', values: { [NEW_TIER.CORE]: '1', [NEW_TIER.STRATEGIC]: 'Up to 5' } },
  { label: 'Calendar audit', values: { [NEW_TIER.CORE]: '30 days', [NEW_TIER.STRATEGIC]: '1 year' } },
  { label: 'Scheduling tools', description: 'Meeting times, logs, links and polls',
    values: { [NEW_TIER.CORE]: true, [NEW_TIER.STRATEGIC]: true } },
  { label: 'CRM (beta)', values: { [NEW_TIER.CORE]: false, [NEW_TIER.STRATEGIC]: true } },
  { label: 'Team collaboration', values: { [NEW_TIER.CORE]: false, [NEW_TIER.STRATEGIC]: true } },
  { label: 'Support Map', description: 'Manage leader and EA assignments',
    values: { [NEW_TIER.CORE]: false, [NEW_TIER.STRATEGIC]: true } },
];

const planOptions: PlanSelectionProps['planOptions'] = [
  {
    id: NEW_TIER.CORE,
    name: 'Core',
    costMonthly: NEW_TIER_DETAILS[NEW_TIER.CORE].monthlyPrice,
    // costAnnually is per month when billed annually
    costAnnually: NEW_TIER_DETAILS[NEW_TIER.CORE].annualPrice / 12,
  },
  {
    id: NEW_TIER.STRATEGIC,
    name: 'Strategic',
    costMonthly: NEW_TIER_DETAILS[NEW_TIER.STRATEGIC].monthlyPrice,
    // costAnnually is per month when billed annually
    costAnnually: NEW_TIER_DETAILS[NEW_TIER.STRATEGIC].annualPrice / 12,
  },
];
const handleTrackPurchase = (
  value: number, currency: string, tier: string, quantity: number, confirmation: string
) => {
  const purchaseData = {
    transaction_id: confirmation,
    value: value * quantity,
    currency,
    items: [{
      id: 0,
      name: tier,
      brand: "Cabinet",
      category: "Premium Subscription",
      quantity,
      price: value,
    }],
  };
  trackPurchase(purchaseData);
  trackEventWithExtra({ eventName: EVENT_TYPE.PURCHASE, extra: purchaseData });
};


export interface PlanSelectionContainerProps {
  onBack?: () => void;
  onFinish: () => void;
  options?: { confirmation?: string; qty?: number; sessionId?: string; val?: number; cur?: string; tier?: string };
}

const PlanSelectionContainer = ({ onBack, onFinish, options }: PlanSelectionContainerProps): ReactElement => {
  const dispatch = useDispatch<ThunkDispatchType>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [dialogError, setDialogError] = useState<string | undefined>();
  const [defaultSelection, setDefaultSelection] = useState<{ planTier?: TIER, billedAnnually?: boolean } | undefined>();
  const logout = useCallback(async () => {
    await dispatch(actions.auth.logout());
    navigate(PAGE_URL.LOGIN);
  }, [dispatch, navigate]);


  const handlePaymentStartFailed = () => {
    setDialogError(`Uh oh - Something went wrong! Try again \
                    or send an SOS to our team at help@joincabinet.com.`);
  };

  const checkDefaultSelection = useCallback(async () => {
    const { value: planTier } = await Preferences.get({ key: 'planTier' });
    const { value: planInterval } = await Preferences.get({ key: 'planInterval' });
    const defaults: { planTier?: TIER, billedAnnually?: boolean } = {};
    if (planTier) {
      defaults.planTier = planTier as TIER;
    }
    if (planInterval) {
      defaults.billedAnnually = planInterval === BILLING_INTERVAL.YEAR;
    }
    setDefaultSelection(defaults);
  }, []);

  useEffect(() => {
    checkDefaultSelection();
  }, [checkDefaultSelection]);

  // finish this step if checkout was successful
  useEffect(() => {
    if (options) {
      // TODO: verify this is working
      const { confirmation, qty, sessionId, val, cur, tier } = options;
      if (confirmation && qty && sessionId && val != null && cur && tier) {
        onFinish();
        handleTrackPurchase(val, cur, tier, qty, confirmation);
      }
    }
  }, [options, onFinish]);

  const handleFinish = async (values: FormInput) => {
    const { billedAnnually, planTier } = values;
    const billingInterval = billedAnnually ? BILLING_INTERVAL.YEAR : BILLING_INTERVAL.MONTH;

    trackEventWithExtra({ 
      eventName: EVENT_TYPE.ONBOARD_CONTINUE_WITH_PLAN, 
      extra: {tier: planTier} 
    });

    if (planTier === NEW_TIER.BASIC) {
      onFinish();
    } else if ([NEW_TIER.CORE, NEW_TIER.STRATEGIC].includes(planTier)) {
      // Coupon codes are available in the Stripe Checkout UI for first-time purchases so we don't handle it here
      // Right now we don't handle more than 1 seat purchases during onboarding
      const quantity = 1;
      const cancelUrl = `${window.location.origin}${PAGE_URL.SIGNUP}?stripeCancel=true`;
      try {
        const session = await getCheckoutSessionId(
          '', planTier, billingInterval, quantity, window.location.href, PAGE_URL.SIGNUP, cancelUrl
        );
        if (session?.status === 200) {
          if (!session.data.active_sub_id && session.data.session_id) {
            await redirectToCheckout(session.data.session_id);
          } else if (session.data.active_sub_id) {
            const result = await dispatch(actions.organization.changeSubscription(
              planTier, billingInterval, quantity, null,
            ));
            if (result?.status === 200) {
              onFinish();
            } else {
              handlePaymentStartFailed();
            }
          } else {
            handlePaymentStartFailed();
          }
        } else {
          handlePaymentStartFailed();
        }
        
      } catch (error) {
        handlePaymentStartFailed();
      }
    } else {
      window.open(PRICING, '_blank');
    }
  };

  return (
    <PlanSelection
      onBack={onBack}
      account={user ? {
        name: [user.first_name, user.last_name].filter(Boolean).join(' '), email: user.email,
      } : undefined}
      defaultSelection={defaultSelection}
      planOptions={planOptions}
      comparisonRows={comparisonRows}
      dialogError={dialogError}
      onDismissError={() => setDialogError(undefined)}
      onFinish={handleFinish}
      onLogout={logout}
    />
  );
};

export default PlanSelectionContainer;
